import React, {
  type ComponentType,
  useCallback,
  useMemo,
} from 'react';
import { type IAdsCopilotProps, useAdsCopilotPilotMap } from '@bingads-webui/ads-copilot-ui';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { LiveAgentConnectionState } from '../../helpers/live-agent-helper';
import { LiveAgentOnlyModeHeader, LiveAgentChatBanner } from '../live-agent-banner';
import { LiveAgentStopConnection } from '../live-agent-stop-connection';
import { TypingIndicator } from '../typing-indicator';

// TODO: Uncomment this when all live agent dependency props are removed
// type PickIAdsCopilotProps = Pick<IAdsCopilotProps, 'botServiceConfig' | 'renderActivity' | 'renderTopComponent' | 'renderBottomComponent'>;

export interface WithLiveAgentProps extends IAdsCopilotProps {
  isLiveAgentOnlyMode?: boolean;
  shouldResumeConversation: { current: boolean };
  liveAgentState: LiveAgentConnectionState;
  isLiveAgentTyping: boolean;
  closeLiveAgentChat: (shouldResumeConversation: boolean) => void;
  onEndLiveAgentChatClick?: () => void;
}

export const withLiveAgent = <T extends WithLiveAgentProps>(WrappedComponent: ComponentType<T>): ComponentType<T> => {
  const HOC = (props: T) => {
    const {
      isLiveAgentOnlyMode,
      isCopilotDegraded,
      liveAgentState,
      isLiveAgentTyping,
      renderTopComponent,
      renderBottomComponent,
      onEndLiveAgentChatClick,
      onClose,
      showViewPrompt,
    } = props;

    const { isRefreshUIEnabled } = useAdsCopilotPilotMap();

    const handleRenderHeaderComponent = useCallback<NonNullable<IAdsCopilotProps['renderHeaderComponent']>>(() => (
      <>
        {isLiveAgentOnlyMode && <LiveAgentOnlyModeHeader headerText={_TL_('Microsoft Advertising Support')} onClose={onClose} />}
        {liveAgentState === LiveAgentConnectionState.CONNECTED && <LiveAgentChatBanner onEndChatClick={onEndLiveAgentChatClick} />}
      </>
    ), [isLiveAgentOnlyMode, liveAgentState, onClose, onEndLiveAgentChatClick]);

    const handleRenderTopComponent = useMemo(() => (!isLiveAgentOnlyMode ? renderTopComponent : undefined), [isLiveAgentOnlyMode, renderTopComponent]);

    const handleRenderBottomComponent = useCallback<NonNullable<IAdsCopilotProps['renderBottomComponent']>>(currentActivities => (
      <>
        {liveAgentState === LiveAgentConnectionState.CONNECTED && isLiveAgentTyping && <TypingIndicator />}
        {renderBottomComponent && renderBottomComponent(currentActivities)}
      </>
    ), [isLiveAgentTyping, liveAgentState, renderBottomComponent]);

    const handleRenderFooterComponent = useCallback<NonNullable<IAdsCopilotProps['renderFooterComponent']>>(() => (
      liveAgentState === LiveAgentConnectionState.IN_PROGRESS && !isRefreshUIEnabled && <LiveAgentStopConnection onStopConnectionClick={onEndLiveAgentChatClick} />
    ), [liveAgentState, onEndLiveAgentChatClick]);

    const shouldShowViewPrompt = useMemo(() => {
      if (isCopilotDegraded || isLiveAgentOnlyMode) {
        return false;
      }
      return liveAgentState === LiveAgentConnectionState.DISCONNECTED;
    }, [isCopilotDegraded, isLiveAgentOnlyMode, liveAgentState]);

    const shouldDisableInput = useMemo(() => isLiveAgentOnlyMode && liveAgentState === LiveAgentConnectionState.DISCONNECTED, [isLiveAgentOnlyMode, liveAgentState]);

    const isConnectedToLiveAgent = useMemo(() => liveAgentState === LiveAgentConnectionState.CONNECTED, [liveAgentState]);

    return (
      <WrappedComponent
        {...props}
        showViewPrompt={showViewPrompt && shouldShowViewPrompt}
        renderHeaderComponent={handleRenderHeaderComponent}
        showHeader={!isLiveAgentOnlyMode}
        renderTopComponent={handleRenderTopComponent}
        renderBottomComponent={handleRenderBottomComponent}
        renderFooterComponent={handleRenderFooterComponent}
        shouldDisableInput={shouldDisableInput}
        isConnectedToLiveAgent={isConnectedToLiveAgent}
        isLiveAgentInProgress={liveAgentState === LiveAgentConnectionState.IN_PROGRESS}
        onEndLiveAgentChatClick={onEndLiveAgentChatClick}
      />
    );
  };

  HOC.displayName = `withLiveAgent(${getDisplayName(WrappedComponent)})`;

  return HOC;
};
